import { useEffect, useState } from "react";
import "./css/dalgoTBrightness.scss";

const formatMinutes = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

export const getSeoulClock = (date: Date) => {
  const local = new Date(date.getTime() + 9 * 60 * 60 * 1000);
  return {
    date: local.toISOString().slice(0, 10),
    minutes: local.getUTCHours() * 60 + local.getUTCMinutes(),
  };
};

export const getSeoulSunset = (date: string) => {
  const dayNumber = (Date.parse(`${date}T00:00:00Z`) - Date.parse(`${date.slice(0, 4)}-01-01T00:00:00Z`)) / 86400000 + 1;
  const radians = Math.PI / 180;
  const approximateDay = dayNumber + (18 - 126.978 / 15) / 24;
  const anomaly = 0.9856 * approximateDay - 3.289;
  const longitude = ((anomaly + 1.916 * Math.sin(anomaly * radians) + 0.020 * Math.sin(2 * anomaly * radians) + 282.634) % 360 + 360) % 360;
  const ascensionBase = ((Math.atan(0.91764 * Math.tan(longitude * radians)) / radians) % 360 + 360) % 360;
  const ascension = (ascensionBase + Math.floor(longitude / 90) * 90 - Math.floor(ascensionBase / 90) * 90) / 15;
  const declination = Math.asin(0.39782 * Math.sin(longitude * radians));
  const hourAngle = Math.acos((Math.cos(90.833 * radians) - Math.sin(declination) * Math.sin(37.5665 * radians)) / (Math.cos(declination) * Math.cos(37.5665 * radians))) / radians / 15;
  const utcHour = hourAngle + ascension - 0.06571 * approximateDay - 6.622 - 126.978 / 15;
  return Math.round((((utcHour + 9) % 24 + 24) % 24) * 60);
};

export const useSeoulSunset = () => {
  const [clock, setClock] = useState(() => getSeoulClock(new Date()));
  useEffect(() => {
    const timer = window.setInterval(() => setClock(getSeoulClock(new Date())), 10000);
    return () => window.clearInterval(timer);
  }, []);
  return { ...clock, sunset: getSeoulSunset(clock.date) };
};

interface BrightnessVehicle {
  id: number;
  name: string;
  autoBrightnessEnabled: boolean;
}

interface BrightnessSettings {
  cd: number;
  manualVehicleProximity: boolean;
  previewTime: string | null;
  sunsetTime: string;
}

const settingsUrl = `${process.env.REACT_APP_URL}/dalgot/operations/brightness`;
const settingsHeaders = {
  "Content-Type": "application/json",
  key: "$2b$10$oEqDXe.5Sq6Vm2dUYnMxfuXDm2Z9CPG9oDA3GjEVitVhiaZzdb.v.",
};

const DalgoTBrightness = ({ vehicles }: { vehicles: BrightnessVehicle[] }) => {
  const clock = useSeoulSunset();
  const [preview, setPreview] = useState<number | null>(null);
  const [settings, setSettings] = useState<BrightnessSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [loadError, setLoadError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetch(settingsUrl, { headers: settingsHeaders, signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("설정을 불러오지 못했습니다.");
        const saved: BrightnessSettings = await response.json();
        setSettings(saved);
        setPreview(saved.previewTime === null ? null : Number(saved.previewTime.slice(0, 2)) * 60 + Number(saved.previewTime.slice(3)));
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setLoadError(true);
        setMessage(error.message);
      });
    return () => controller.abort();
  }, []);

  const saveSettings = async (changes: Partial<Pick<BrightnessSettings, "previewTime" | "manualVehicleProximity">>) => {
    setSaving(true);
    setMessage("");
    try {
      const response = await fetch(settingsUrl, {
        method: "POST",
        headers: settingsHeaders,
        body: JSON.stringify(changes),
      });
      if (!response.ok) throw new Error("설정 저장에 실패했습니다. 다시 시도해 주세요.");
      const saved: BrightnessSettings = await response.json();
      setSettings(saved);
      if ("previewTime" in changes) {
        setPreview(saved.previewTime === null ? null : Number(saved.previewTime.slice(0, 2)) * 60 + Number(saved.previewTime.slice(3)));
      }
      setMessage("저장했습니다.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "설정 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };
  const selectedMinutes = preview ?? clock.minutes;
  const daytime = selectedMinutes >= 540 && selectedMinutes < clock.sunset;
  const brightness = daytime ? 2000 : 200;
  const sunsetLabel = formatMinutes(clock.sunset);

  return (
    <section className="dalgo-brightness" aria-label="자동 밝기 스케줄">
      <header>
        <div><h2>자동 밝기 스케줄</h2><p>09:00 ~ 일몰 2,000cd/㎡ · 일몰 이후 ~ 09:00 200cd/㎡ (서울 일몰 기준 자동 전환)</p></div>
        <span>{clock.date.replace(/-/g, ".")} · SEOUL</span>
      </header>
      <div className="dalgo-brightness__facts">
        <span>오늘 서울 일몰 <strong>{sunsetLabel}</strong></span>
        <span><i className="is-day" /> 주간 <strong>2,000</strong> cd/㎡</span>
        <span><i /> 야간 <strong>200</strong> cd/㎡</span>
        <span>현재 시각(KST) <strong>{formatMinutes(clock.minutes)}</strong></span>
      </div>
      <div className="dalgo-brightness__timeline">
        <div className="dalgo-brightness__track" style={{ background: `linear-gradient(to right, #2d2b49 0%, #2d2b49 37.5%, #f2b32c 37.5%, #f2b32c ${clock.sunset / 14.4}%, #2d2b49 ${clock.sunset / 14.4}%, #2d2b49 100%)` }} />
        <span className="dalgo-brightness__cursor" style={{ left: `${selectedMinutes / 14.4}%` }}>{formatMinutes(selectedMinutes)}</span>
        <span className="dalgo-brightness__tick is-start">00:00</span>
        <span className="dalgo-brightness__tick" style={{ left: "37.5%" }}><strong>09:00</strong><small>2,000 시작</small></span>
        <span className="dalgo-brightness__tick" style={{ left: `${clock.sunset / 14.4}%` }}><strong>{sunsetLabel}</strong><small>일몰 · 200 전환</small></span>
        <span className="dalgo-brightness__tick is-end">24:00</span>
      </div>
      <div className="dalgo-brightness__vehicles">
        {vehicles.map((vehicle) => (
          <article key={vehicle.id}>
            <h3>{vehicle.name}</h3>
            <div className="dalgo-brightness__gauge">
              <svg viewBox="0 0 200 120" aria-label={`${vehicle.name} ${vehicle.autoBrightnessEnabled ? `${brightness} cd/㎡` : "자동 밝기 꺼짐"}`} role="img">
                <path d="M 20 100 A 80 80 0 0 1 180 100" pathLength="100" className="gauge-base" />
                <path d="M 20 100 A 80 80 0 0 1 180 100" pathLength="100" className={`gauge-value${daytime ? " is-day" : ""}`} strokeDasharray={`${vehicle.autoBrightnessEnabled ? brightness / 20 : 0} 100`} />
                <text x="8" y="119">0</text><text x="177" y="119">2,000</text>
              </svg>
              <div><strong>{vehicle.autoBrightnessEnabled ? brightness.toLocaleString() : "—"}</strong><small>cd/㎡</small></div>
            </div>
            <span className={`dalgo-brightness__state${daytime ? " is-day" : ""}`}>{vehicle.autoBrightnessEnabled ? (daytime ? "주간 모드 (09:00~일몰)" : "야간 모드 (일몰~09:00)") : "자동 밝기 OFF"}</span>
          </article>
        ))}
      </div>
      <div className="dalgo-brightness__preview">
        <label htmlFor="dalgo-brightness-time">시각 미리보기</label>
        <input id="dalgo-brightness-time" type="range" min="0" max="1439" value={selectedMinutes} disabled={!settings || saving} onChange={(event) => setPreview(Number(event.target.value))} aria-valuetext={formatMinutes(selectedMinutes)} />
        <output htmlFor="dalgo-brightness-time">{formatMinutes(selectedMinutes)}</output>
        <button type="button" disabled={!settings || saving} onClick={() => saveSettings({ previewTime: preview === null ? null : formatMinutes(preview) })}>시각 저장</button>
        <button type="button" disabled={!settings || saving} onClick={() => saveSettings({ previewTime: null })}>현재 시각으로</button>
      </div>
      <div className="dalgo-brightness__manual">
        <div><strong>수동 차량 근접</strong><p>변경한 상태는 저장 즉시 조회 API에 반영됩니다.</p></div>
        <button type="button" role="switch" aria-checked={settings?.manualVehicleProximity ?? false} aria-label="수동 차량 근접" disabled={!settings || saving} className={`dalgo-console__toggle${settings?.manualVehicleProximity ? " is-on" : ""}`} onClick={() => saveSettings({ manualVehicleProximity: !settings?.manualVehicleProximity })}><i aria-hidden="true" /><span>{settings?.manualVehicleProximity ? "ON" : "OFF"}</span></button>
      </div>
      <p className="dalgo-brightness__save-status" role="status">{saving ? "저장 중…" : message || (settings ? `API 적용 시각: ${settings.previewTime ?? "현재 시각"} · 저장된 설정은 모든 조회에 공통 적용됩니다.` : "설정 불러오는 중…")}</p>
      {loadError && <button type="button" onClick={() => window.location.reload()}>다시 불러오기</button>}
      <p className="dalgo-brightness__note">일몰시간은 서울(위도 37.5665°N, 경도 126.978°E) 기준 천문 계산으로 매일 자동 갱신됩니다. 공식 천문 자료와 약 1~2분 차이가 있을 수 있습니다. 밝기는 선택한 시각의 화면 미리보기입니다.</p>
    </section>
  );
};

export default DalgoTBrightness;
